<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useProfileStore } from '@/stores/profile'
import { useTranslaterStore } from '@/stores/translater'
import { useCarEventsStore } from '@/stores/car-events'
import { useAreasStore } from '@/stores/areas'
import { useLoadYMapScript } from '@/composables/useLoadYMapScript.js'
import Modal from '@/components/UTAModal.vue'
import UTALogsMap from '@/components/UTALogsMap.vue'
import UTACarEventsSubscribe from '@/components/car/UTACarEventsSubscribe.vue'
import checkArrayIncludes from '@/helpers/checkArrayIncludes'
import calcDatas from '@/helpers/calcDatas'

const { mapLoader, mapCrash, mapReady } = useLoadYMapScript()

function dateToTimestamp(data) {
    return Math.round(new Date(data).getTime()) / 1000
}

function changePage(cursor, dir) {

    const params = {
        'c.limit': 12,
        'c.order': 'DESC',
        'c.direction': dir, 
        'c.cursor': cursor,
        uuid_car_array: props.uuid,
        start: dateToTimestamp(UTACarEventsFrom.value),
        stop: dateToTimestamp(UTACarEventsTill.value)
    }

    if(carEvent.value) {
        params.type_array = carEvent.value
    }

    carEventsStore.readEventsCar(params).then(() => {
        if(eventsCarPart.value?.array?.length) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }

        // обновить данные геозон
        loadEventAreas()
    })
}

function eventsFormSubmit() {

    let data = {

        uuid_car_array: props.uuid,
        'c.limit': 12, 
        'c.order': 'DESC'
    }

    const from = dateToTimestamp(UTACarEventsFrom.value)
    const till = dateToTimestamp(UTACarEventsTill.value)

    if(from && till) {

        if(from < till) {

            if(till - from < 2764800) {

                data.start = from
                data.stop = till

                if (carEvent.value) data.type_array = carEvent.value

                carEventsStore.readEventsCar(data).then(() => {

                    if(eventsCarPartMsg.value.success) {
                        pageNumber.value = 1
                    }

                    // обновить данные геозон
                    loadEventAreas()
                })

            } else {
                carEventsStore.setEventsCarPartMsg({ success: false, text: 'CAR_EVENTS_ERROR_MAX_PERIOD' })
            }

        } else {
            carEventsStore.setEventsCarPartMsg({ success: false, text: 'CAR_EVENTS_ERROR_INVALID_LIMITS' })
        }

    } else {
        carEventsStore.setEventsCarPartMsg({ success: false, text: 'CAR_EVENTS_ERROR_NO_LIMITS' })
    }
}

// Загрузка геозон событий
function loadEventAreas() {

    eventsCarPart.value?.array?.map(eventCar => {
        if(eventCar.type == 'protected_area' || eventCar.type == 'protected_area_in') {
            eventAreas.value[eventCar.area] = null
        }
    })

    if(Object.keys(eventAreas.value).length) {

        const uuids = Object.keys(eventAreas.value)
        const payload = {
            'c.limit': 1000, 
            uuid_array: uuids 
        }
        const isUseStore = false

        areasStore.readAreas(payload, isUseStore).then(areas => {
            areas?.array?.forEach(area => {
                eventAreas.value[area.uuid] = area
            })
        })
    }
}

function showEventsMap(item) {
    if(eventAreas.value[item.area]) eventArea.value = eventAreas.value[item.area]
    
    coords.value[0] = item.coords.lon
    coords.value[1] = item.coords.lat
    showEventsOnMap.value = true
}

function hideEventsMap() {
    showEventsOnMap.value = false
    eventArea.value = null
}

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const profileStore      = useProfileStore()
const translaterStore   = useTranslaterStore()
const carEventsStore    = useCarEventsStore()
const areasStore        = useAreasStore()

const profile                   = computed(() => profileStore.profile)
const getTranslate              = computed(() => translaterStore.getTranslate)
const eventsCarPart             = computed(() => carEventsStore.eventsCarPart)
const eventsCarPartProcess      = computed(() => carEventsStore.eventsCarPartProcess)
const eventsCarPartMsg          = computed(() => carEventsStore.eventsCarPartMsg)
const UTACarEventsMaxDateFrom   = computed(() => calcDatas('tm', new Date().getTime()))
const UTACarEventsMaxDateTill   = computed(() => calcDatas('tEnd'))

const UTACarEventsFrom      = ref(calcDatas('month'))
const UTACarEventsTill      = ref(calcDatas('tEnd'))
const pageNumber            = ref(1)
const showEventsOnMap       = ref(false)
const coords                = ref([])
const carEvent              = ref('')
const eventAreas            = ref({})
const eventArea             = ref(null)

const carEvents = [
    {
        value: '',
        title: 'EVENT_ALL'
    },
    {
        value: 'engine_on',
        title: 'EVENT_ENGINE_ON'
    },
    {
        value: 'engine_off', 
        title: 'EVENT_ENGINE_OFF'
    },
    {
        value: 'protected_area_in', 
        title: 'EVENT_PROTECTED_AREA_IN'
    },
    {
        value: 'protected_area', 
        title: 'EVENT_PROTECTED_AREA'
    }
]

carEventsStore.readEventsCar({

    uuid_car_array: props.uuid,
    'c.limit': 12,
    'c.order': 'DESC',
    start: dateToTimestamp(UTACarEventsFrom.value),
    stop: dateToTimestamp(UTACarEventsTill.value)
}).then(() => {
    // обновить данные геозон
    loadEventAreas()
})

onBeforeUnmount(() => {

    carEventsStore.setEventsCarPart({})
    carEventsStore.setEventsCarPartMsg({})
})

</script>

<template>

    <div class="uta-car-events">

        <header class="page__header uta-header" role="heading" aria-level="1">
            <h2 class="uta-header__title">{{ getTranslate.CAR_EVENTS }}</h2>
            <div class="spinner-border header__spinner" role="status" v-if="eventsCarPartProcess && eventsCarPart?.array?.length">
                <span class="visually-hidden">Loading...</span>
            </div>
        </header>

        <UTACarEventsSubscribe :carUuid="props.uuid" />

        <form action="" class="mb-4 uta-car-events__form" @submit.prevent="eventsFormSubmit()">
            <div class="row d-flex align-items-end">
                <div class="form-group col-12 col-sm-6 col-md-4 col-xxl-3 mb-4">
                    <label for="UTACarEventsStart">{{ getTranslate.CAR_EVENTS_START }}:</label>
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarEventsFrom"
                        :max="UTACarEventsMaxDateFrom"
                        v-model="UTACarEventsFrom"
                    >
                </div>
                <div class="form-group col-12 col-sm-6 col-md-4 col-xxl-3 mb-4">
                    <label for="UTACarEventsStop">{{ getTranslate.CAR_EVENTS_END }}:</label>
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarEventsTill"
                        :max="UTACarEventsMaxDateTill"
                        v-model="UTACarEventsTill"
                    >
                </div>
                <div class="form-group col-12 col-sm-6 col-md-4 col-xxl-3 mb-4">
                    <label for="UTACarEventsFilter">{{ getTranslate.CAR_EVENTS_FILTER }}:</label>
                    <select 
                        class="uta-input uta-input--block" 
                        id="UTACarEventsFilter" 
                        v-model="carEvent"
                    >
                        <option v-for="(option, idx) in carEvents" :key="idx" :value="option.value">{{ getTranslate[option.title] }}</option>   
                    </select>
                </div>
                <div class="col-12 col-sm-6 col-md-4 col-xxl-3 mb-4">
                    <button type="submit" class="uta-btn uta-btn--main uta-btn--block uta-btn--lg uta-car-events__submit" :disabled="eventsCarPartProcess">
                        <template v-if="!eventsCarPartProcess">
                            {{ getTranslate.BTN_REQUEST }}
                        </template>
                        <template v-else>
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </template>
                    </button>
                </div>
            </div>
        </form>

        <div 
            v-if="'success' in eventsCarPartMsg && !eventsCarPartMsg.success"
            class="my-4 uta-alert uta-alert--danger uta-car-events__message" 
        >
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[eventsCarPartMsg.text] || eventsCarPartMsg.text }}
            </div>
        </div>

        <template v-if="eventsCarPart?.array?.length">
            <div class="container-fluid">
                <div class="uta-list uta-car-events__list">
                    <div class="uta-list__title d-none d-md-block uta-cars__title">
                        <div class="row">
                            <div class="col-5">{{ getTranslate.CAR_EVENTS_EVENT }}</div>
                            <div class="col-5">{{ getTranslate.CAR_EVENTS_DATE }}</div>
                        </div>
                    </div>

                    <div class="uta-list__content">
                        <div 
                            class="row align-items-center uta-list__item uta-car-events__event" 
                            :class="{ 'uta-list__item--dark' : index % 2 }" 
                            v-for="(item, index) in eventsCarPart.array" 
                            :key="item.createdAt"
                        >
                            <div class="col-12 col-md-5">
                                <div class="uta-car-events__event-name">
                                    {{ getTranslate[`EVENT_${item.type.toUpperCase()}`] || item.type }}
                                </div>
                                <div class="uta-car-events__event-area mt-2" v-if="item.area">
                                    {{ getTranslate.CAR_AREAS_AREA }}: 
                                    <router-link class="uta-car-events__event-area-link" :to="`/areas/${item.area}`">{{ eventAreas[item.area]?.name }}</router-link>
                                </div>
                            </div>
                            <div class="col-12 col-md-5">
                                <div class="uta-car-events__event-date">
                                    {{ new Date(item.createdAt / 1000).toLocaleString() }}
                                </div>
                            </div>
                            <div class="col-12 col-sm-6 col-md-2" v-if="checkArrayIncludes(profile.attr?.array, ['GEODATA', 'SUPER_ADMIN'])">
                                <div class="uta-car-events__event-map">
                                    <button class="uta-btn uta-btn--block" @click="showEventsMap(item)">{{ getTranslate.CAR_EVENTS_MAP }}</button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="uta-cars__pagination mt-5" v-if="eventsCarPart.c && (eventsCarPart.c.isBefore || eventsCarPart.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                        <div class="uta-pagination">
                            <button 
                                :disabled="eventsCarPartProcess || !eventsCarPart.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(eventsCarPart.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="eventsCarPartProcess || !eventsCarPart.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(eventsCarPart.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <transition name="bounce">
            <Modal 
                v-if="showEventsOnMap"
                :header="getTranslate.CAR_EVENTS_MAP"
                size="lg"
                @closeModal="hideEventsMap"
            >
                <template v-slot:body>
                    <div v-if="mapLoader" class="w-100 h-100 d-flex justify-content-center align-items-center">
                        <div class="spinner-border text-secondary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div v-else>
                        <div class="uta-logs-map-crash" v-if="mapCrash">
                            <div>{{ getTranslate.CARMAP_CRASH }}</div>
                        </div>
                        <UTALogsMap v-if="mapReady" :coords="coords" :area="eventArea" size="md" />
                    </div>
                </template>
            </Modal>
        </transition>

    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-events.scss';
</style>